export const NEW_PASSWORD_FIELDS = ['next', 'confirm'] as const;
export type NewPasswordField = (typeof NEW_PASSWORD_FIELDS)[number];

export interface NewPasswordValues {
  readonly next: string;
  readonly confirm: string;
}

export interface NewPasswordErrors {
  readonly next: 'tooShort' | null;
  readonly confirm: 'mismatch' | null;
}

export interface NewPasswordReveal {
  readonly touched: Readonly<Record<NewPasswordField, boolean>>;
  readonly submitted: boolean;
}

export type PasswordRuleState = 'pending' | 'met' | 'failed';

export const UNTOUCHED_NEW_PASSWORD: Readonly<Record<NewPasswordField, boolean>> = { next: false, confirm: false };

export const meetsLengthRule = (next: string, minLength: number): boolean => next.length >= minLength;

export const newPasswordFailures = ({ next, confirm }: NewPasswordValues, minLength: number): NewPasswordErrors => ({
  next: meetsLengthRule(next, minLength) ? null : 'tooShort',
  confirm: confirm === next ? null : 'mismatch',
});

export const newPasswordPasses = (values: NewPasswordValues, minLength: number): boolean => {
  const failures = newPasswordFailures(values, minLength);
  return failures.next === null && failures.confirm === null;
};

export const newPasswordErrors = (values: NewPasswordValues, { touched, submitted }: NewPasswordReveal, minLength: number): NewPasswordErrors => {
  const failures = newPasswordFailures(values, minLength);
  const confirmJudged = submitted || touched.confirm || values.confirm.length >= values.next.length;
  return {
    next: submitted || touched.next ? failures.next : null,
    confirm: confirmJudged ? failures.confirm : null,
  };
};

export const passwordRuleState = (errors: NewPasswordErrors, values: NewPasswordValues, minLength: number): PasswordRuleState => {
  if (errors.next !== null || errors.confirm !== null) return 'failed';
  return newPasswordPasses(values, minLength) ? 'met' : 'pending';
};

export const firstFailingNewPasswordField = (errors: NewPasswordErrors): NewPasswordField | null =>
  NEW_PASSWORD_FIELDS.find((field) => errors[field] !== null) ?? null;
